import { useEffect, useState } from 'react';
import {
  getDiagnosticHistory,
  getLatestDiagnostic,
  type AggregatedDiagnostic,
  type PercentilesPerHotPath,
  type SessionDiagnosticRaw,
} from '../../services/diagnostics';
import {
  getFleetSchedulerHealth,
  type FleetSchedulerHealth,
} from '../../services/schedulerHealth';
import {
  classificarDetectorDaSessao,
  corDaSaude,
  formatarDuracao,
  ordenarDetectores,
  rotuloDaSaude,
  rotuloDoPapel,
  veredicto,
} from '../../lib/detectorHealth';

// Sprint 29 / Fase 29.F — Painel admin page pra perf telemetry.
//
// Operador busca por session_uuid pra ver:
//   - Snapshot mais recente com percentis p50/p95/p99/max por hot path
//   - Métricas de processo (RSS, CPU times, thread count, uptime)
//   - Time-series histórica (últimos N snapshots)
//
// Read-only — não permite mutation. Endpoints retornam JSONB direto da DB.
//
// ------------------------------------------------------------
// 2026-08-05 — SAÚDE DO ESCALONAMENTO, NO TOPO E SEM BUSCA
// ------------------------------------------------------------
// Até aqui esta página abria num campo de busca vazio: só servia a quem JÁ
// suspeitava de uma sessão. O bug que motivou este bloco — ProcessScanner
// segurando a thread serial por 262,9 s e deixando cinco detectores de cadência
// 1 s parados por 4 min e meio — é justamente o tipo de coisa de que ninguém
// suspeita, porque não há vítima reclamando. Por isso o veredito de FROTA
// carrega no mount, sem UUID: o sintoma aparece em vez de precisar ser
// procurado.
//
// ⛔ Isto é DIAGNÓSTICO OPERACIONAL. Nenhum número desta seção acusa jogador,
// pontua risco ou entra em família de evidência — e a tela diz isso em letras,
// porque o próximo a olhar vai ser tentado a "aproveitar o dado". Ver o bloco
// no topo de lib/detectorHealth.ts.

function formatNumber(n: number | undefined, decimals = 0): string {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return decimals === 0 ? n.toString() : n.toFixed(decimals);
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function HotPathRow({ hp }: { hp: PercentilesPerHotPath }) {
  return (
    <tr className="border-b border-[#2a2f36] hover:bg-[#1a1f26]">
      <td className="py-2 px-3 font-mono text-xs text-amber-400">{hp.hotpath_name}</td>
      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(hp.sample_count)}</td>
      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(hp.p50_us)}</td>
      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(hp.p95_us)}</td>
      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(hp.p99_us)}</td>
      <td className="py-2 px-3 text-right text-red-400">{formatNumber(hp.max_us)}</td>
    </tr>
  );
}

const JANELAS = [
  { minutos: 15, rotulo: '15 min' },
  { minutos: 60, rotulo: '1 h' },
  { minutos: 360, rotulo: '6 h' },
  { minutos: 1440, rotulo: '24 h' },
];

function SchedulerHealthSection() {
  const [janela, setJanela] = useState(60);
  const [fleet, setFleet] = useState<FleetSchedulerHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFleetSchedulerHealth(janela)
      .then((f) => { if (!cancelled) setFleet(f); })
      .catch((e) => {
        if (cancelled) return;
        // Falha de leitura NÃO pode virar tabela vazia: vazio, nesta tela, lê
        // como "nenhum detector atrasou" — uma afirmação de saúde construída a
        // partir de um erro. Zera o dado e mostra o erro.
        setFleet(null);
        setError(e?.response?.data?.error?.message || e?.message || 'erro desconhecido');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [janela]);

  const v = veredicto(fleet);
  const linhas = fleet ? ordenarDetectores(fleet.detectors ?? []) : [];

  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 className="text-lg font-bold text-amber-400">
          Saúde do escalonamento — frota
        </h2>
        <div className="flex gap-1">
          {JANELAS.map((j) => (
            <button
              key={j.minutos}
              type="button"
              onClick={() => setJanela(j.minutos)}
              className={`px-2 py-1 rounded text-xs border transition ${
                janela === j.minutos
                  ? 'border-amber-500/50 bg-amber-500/10 text-amber-200'
                  : 'border-[#2a2f36] text-gray-400 hover:text-gray-200'
              }`}
            >
              {j.rotulo}
            </button>
          ))}
        </div>
      </div>

      {/* A ressalva vem ANTES do número, não numa nota de rodapé: é a primeira
          coisa que o próximo leitor precisa saber sobre este bloco. */}
      <p className="text-xs text-gray-500 mb-3">
        Diagnóstico operacional da nossa varredura. Detector lento é problema
        nosso — nada aqui acusa jogador, pontua risco ou vira evidência.
      </p>

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded">
          <span className="font-semibold">Não foi possível ler a saúde do escalonamento:</span>{' '}
          {error}
          <div className="text-xs text-red-200/70 mt-1">
            Sem esta leitura não dá pra afirmar que a frota está bem — o silêncio
            aqui não é boa notícia.
          </div>
        </div>
      )}

      {loading && !error && (
        <p className="text-sm text-gray-500">Lendo a frota…</p>
      )}

      {!loading && !error && (
        <>
          <div className={`px-4 py-3 rounded border mb-4 ${corDaSaude(v.saude)}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${corDaSaude(v.saude)}`}
              >
                {/* v.rotulo, NÃO rotuloDaSaude(v.saude): nos estados "não sabemos"
                    (sem_snapshot/ninguem_reporta/dados_incompletos) `v.saude` vale
                    'ok' só pela cor cinza — rotuloDaSaude('ok') diria "dentro da
                    cadência", uma afirmação de saúde que os dados não sustentam.
                    v.rotulo já vem escolhido por estado em detectorHealth.ts. */}
                {v.rotulo}
              </span>
              <span className="font-semibold">{v.frase}</span>
            </div>
            <p className="text-sm mt-1 opacity-80">{v.detalhe}</p>
          </div>

          {fleet && (
            <p className="text-xs text-gray-500 mb-3">
              {/* "com detalhe por detector" virou "de SDK que sabe atribuir" em
                  2026-08-06 (tarde), e não é preciosismo de redação: com o
                  formato delta, um SDK que sabe atribuir e não teve ninguém
                  atrasado manda a lista VAZIA. A frase antiga contaria a frota
                  mais saudável como frota sem detalhamento. */}
              Janela de {fleet.window_minutes} min · {fleet.snapshots} snapshots
              de {fleet.sessions} sessões · {fleet.snapshots_reporting} com bloco
              de escalonamento · {fleet.snapshots_attributed} de SDK que sabe
              atribuir por detector.
            </p>
          )}

          {linhas.length > 0 && (
            <div className="overflow-x-auto bg-[#161a1f] rounded border border-[#2a2f36]">
              <table className="w-full text-sm">
                <thead className="bg-[#0d0f12] text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="py-2 px-3 text-left">Detector</th>
                    <th className="py-2 px-3 text-left">Situação</th>
                    <th
                      className="py-2 px-3 text-left"
                      title="A varredura é serial: quem tem tick longo segura os outros. Quem só atrasou é vítima, não causa."
                    >
                      Papel
                    </th>
                    {/* A coluna "Cadência" MORREU aqui em 2026-08-06 (tarde).
                        Ela lia `cadence_ms`, que saiu do wire porque publicava
                        de quanto em quanto tempo cada checagem roda. Deixar a
                        coluna imprimindo `formatarDuracao(0)` = "0" seria a tela
                        afirmando cadência zero — pior que não ter a coluna. */}
                    <th
                      className="py-2 px-3 text-right"
                      title="Maior tempo, na janela, em que a frota ficou sem este detector"
                    >
                      Sem rodar (pior)
                    </th>
                    <th
                      className="py-2 px-3 text-right"
                      title="Maior tick individual — enquanto durou, TODOS os outros detectores ficaram parados"
                    >
                      Tick mais longo
                    </th>
                    <th
                      className="py-2 px-3 text-right"
                      title="Sessões em que ESTE detector teve problema, sobre o total de sessões que reportaram escalonamento na janela"
                    >
                      Sessões afetadas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr
                      key={l.dado.detector_name}
                      className="border-b border-[#2a2f36] hover:bg-[#1a1f26]"
                    >
                      <td className="py-2 px-3 font-mono text-xs text-gray-200">
                        {l.dado.detector_name}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${corDaSaude(l.saude)}`}
                        >
                          {rotuloDaSaude(l.saude)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-400">
                        {rotuloDoPapel(l.papel)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-200">
                        {formatarDuracao(l.dado.worst_lateness_ms)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-200">
                        {formatarDuracao(l.dado.worst_run_ms)}
                      </td>
                      {/* DENOMINADOR: o de FROTA, não o do detector. Com formato
                          delta o detector só aparece quando teve problema, então
                          "N de sessions_affected" imprimiria eternamente
                          "N de N" — a tela mentindo por omissão com um número
                          que parece informativo. `sessions_reporting` de frota
                          conta sessões com bloco não-vazio e não depende de
                          roster nenhum, então sobreviveu intacto.

                          ⚠️ 2026-08-06 (noite): o NUMERADOR só virou verdade
                          agora. `sessions_affected` era COUNT(DISTINCT
                          session_uuid) sem filtro de problema, então uma linha
                          de roster LEGADO — detector sem nada de errado — saía
                          daqui como "1 de 1" sob o título "Sessões em que ESTE
                          detector teve problema". Quem garante o título é o
                          WHERE de `kBreakdownSql`
                          (backend/src/services/DiagnosticsService.cpp), não
                          esta tela: se aquele filtro cair, este tooltip volta a
                          mentir e nada aqui perceberia. */}
                      <td className="py-2 px-3 text-right text-gray-400 text-xs">
                        {l.dado.sessions_affected} de {fleet?.sessions_reporting ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function Diagnostics() {
  const [sessionUuid, setSessionUuid] = useState('');
  const [searchedUuid, setSearchedUuid] = useState('');
  const [latest, setLatest] = useState<AggregatedDiagnostic | null>(null);
  const [history, setHistory] = useState<SessionDiagnosticRaw[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!searchedUuid) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setLatest(null);
    setHistory([]);

    Promise.all([
      getLatestDiagnostic(searchedUuid),
      getDiagnosticHistory(searchedUuid, 30),
    ])
      .then(([agg, hist]) => {
        if (cancelled) return;
        setLatest(agg);
        setHistory(hist.data);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.error?.message
          || e?.message
          || 'erro desconhecido';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [searchedUuid]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearchedUuid(sessionUuid.trim());
  }

  return (
    <div className="text-gray-200">
      <h1 className="text-2xl font-bold mb-4">Diagnostics</h1>
      <p className="text-sm text-gray-400 mb-6">
        Perf telemetry do SDK (Sprint 29). O bloco abaixo responde pela frota
        inteira, sem busca; a busca por session_uuid serve pra descer numa
        sessão específica (percentis de hot paths, métricas do processo,
        histórico de snapshots).
      </p>

      <SchedulerHealthSection />

      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <input
          type="text"
          value={sessionUuid}
          onChange={(e) => setSessionUuid(e.target.value)}
          placeholder="session_uuid (UUID v4)"
          className="flex-1 max-w-md bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-2 text-sm font-mono"
        />
        <button
          type="submit"
          disabled={!sessionUuid.trim() || loading}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-2 rounded text-sm font-medium transition"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded mb-6">
          <span className="font-semibold">Erro:</span> {error}
        </div>
      )}

      {latest && (
        <>
          <section className="mb-8">
            <h2 className="text-lg font-bold mb-2 text-amber-400">Snapshot mais recente</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-[#161a1f] p-4 rounded border border-[#2a2f36]">
              <div>
                <span className="text-gray-500 block">Captured at</span>
                <span className="font-mono">{formatDateTime(latest.captured_at)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">SDK version</span>
                <span className="font-mono">{latest.sdk_version || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Build ID</span>
                <span className="font-mono">{latest.build_id || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Sanitized (LGPD)</span>
                <span className={latest.sanitized ? 'text-green-400' : 'text-red-400'}>
                  {latest.sanitized ? 'true' : 'false'}
                </span>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-bold mb-2 text-amber-400">Process metrics</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm bg-[#161a1f] p-4 rounded border border-[#2a2f36]">
              <div>
                <span className="text-gray-500 block">RSS (MB)</span>
                <span className="font-mono">{formatNumber(latest.process_metrics.rss_mb, 2)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">CPU kernel (µs)</span>
                <span className="font-mono">{formatNumber(latest.process_metrics.cpu_kernel_us)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">CPU user (µs)</span>
                <span className="font-mono">{formatNumber(latest.process_metrics.cpu_user_us)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Threads</span>
                <span className="font-mono">{formatNumber(latest.process_metrics.thread_count)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Uptime (s)</span>
                <span className="font-mono">{formatNumber(latest.process_metrics.uptime_s)}</span>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-bold mb-2 text-amber-400">
              Escalonamento nesta sessão
            </h2>
            {/* Esta seção é a ÚNICA das duas (frota × sessão) atrelada a um
                session_uuid — a um jogador concreto. É onde a ressalva mais
                importa, e onde faltava: o bloco de frota, acima, já a tem.
                Sem cor/badge de propósito (ver comentário do bloco abaixo). */}
            <p className="text-xs text-gray-500 mb-3">
              Diagnóstico operacional da nossa varredura, não da conduta deste
              jogador. Detector lento é problema nosso — nada aqui acusa,
              pontua risco ou vira evidência.
            </p>
            {(() => {
              const sh = latest.scheduler_health;
              const dets = sh?.detectors ?? [];
              if (!sh || Object.keys(sh).length === 0) {
                return (
                  <p className="text-sm text-gray-500">
                    Esta sessão rodava um SDK que ainda não reporta saúde de
                    escalonamento. Isso não quer dizer que nada atrasou — quer
                    dizer que não sabemos.
                  </p>
                );
              }
              if (dets.length === 0) {
                const t = sh.totals;
                // ⚠️ 2026-08-06 (tarde) — LISTA VAZIA PASSOU A TER DOIS
                // SIGNIFICADOS OPOSTOS, e `attributed` é o que os separa.
                // Com formato delta, o SDK só lista quem teve problema: lista
                // vazia + `attributed: true` é a MELHOR notícia possível desta
                // tela. O texto antigo ("o SDK reportou os contadores do
                // registry inteiro, sem separar por detector") é o que uma
                // sessão PERFEITA receberia se ninguém lesse o outro campo.
                if (sh.attributed === true) {
                  return (
                    <p className="text-sm text-gray-400">
                      Nenhum detector desta sessão atrasou nem segurou a fila na
                      janela reportada. O SDK sabe dizer de quem quando acontece —
                      a lista está vazia porque não houve, não porque não sabemos.
                      {(t?.max_run_ms ?? 0) > 0 && (
                        <> Pior tick da janela: {formatarDuracao(t?.max_run_ms)}.</>
                      )}
                    </p>
                  );
                }
                return (
                  <p className="text-sm text-gray-400">
                    O SDK reportou os contadores do registry inteiro, sem separar
                    por detector: {t?.late_runs ?? 0} execuções atrasadas, pior
                    atraso {formatarDuracao(t?.max_lateness_ms)}, pior tick{' '}
                    {formatarDuracao(t?.max_run_ms)}. Não dá pra dizer de qual
                    detector — e não vamos chutar.
                  </p>
                );
              }
              return (
                <div className="overflow-x-auto bg-[#161a1f] rounded border border-[#2a2f36]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0d0f12] text-gray-400 text-xs uppercase">
                      <tr>
                        {/* "Cadência" e "Execuções" saíram em 2026-08-06
                            (tarde): as duas liam campos que deixaram de existir
                            no wire, e juntas publicavam o mapa operacional
                            (`runs / window_s` devolve a cadência). Entrou
                            "Situação", que é o veredito de fome que o SDK manda
                            no lugar da cadência. */}
                        <th className="py-2 px-3 text-left">Detector</th>
                        <th className="py-2 px-3 text-left">Situação</th>
                        <th className="py-2 px-3 text-right">Atrasadas</th>
                        <th className="py-2 px-3 text-right">Sem rodar (pior)</th>
                        <th className="py-2 px-3 text-right">Tick mais longo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dets.map((d) => {
                        // ⚠️ 2026-08-06 (noite) — ISTO ERA UM TERNÁRIO INLINE
                        // COM DOIS RAMOS ('faminto' | 'atrasado') E NENHUM
                        // 'ok'. No formato delta estava certo — só sobe quem
                        // teve problema. No formato LEGADO `dets` é o ROSTER
                        // INTEIRO: uma sessão da DLL de 30/06 com 17 entradas,
                        // 15 delas com late_runs=0/slow_runs=0/max_run_ms=3,
                        // pintava as 15 como "fora da cadência". Sessão
                        // perfeita apresentada como 15 detectores com problema.
                        //
                        // O critério agora é UM só, compartilhado com a tabela
                        // de frota (lib/detectorHealth.ts). Era a existência de
                        // DUAS classificações da mesma verdade que permitiu a
                        // divergência — não a falta de um `if`.
                        const saude = classificarDetectorDaSessao(d);
                        return (
                        <tr
                          key={d.name}
                          className="border-b border-[#2a2f36] hover:bg-[#1a1f26]"
                        >
                          <td className="py-2 px-3 font-mono text-xs text-gray-200">{d.name}</td>
                          <td className="py-2 px-3">
                            <span
                              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${corDaSaude(saude)}`}
                            >
                              {rotuloDaSaude(saude)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right text-gray-300">{d.late_runs}</td>
                          <td className="py-2 px-3 text-right text-gray-200">
                            {formatarDuracao(d.max_lateness_ms)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-200">
                            {formatarDuracao(d.max_run_ms)}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-bold mb-2 text-amber-400">Hot paths — percentis</h2>
            {latest.hot_paths.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum hot path com samples nesta janela.</p>
            ) : (
              <div className="overflow-x-auto bg-[#161a1f] rounded border border-[#2a2f36]">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d0f12] text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="py-2 px-3 text-left">Hot path</th>
                      <th className="py-2 px-3 text-right">Samples</th>
                      <th className="py-2 px-3 text-right">p50 (µs)</th>
                      <th className="py-2 px-3 text-right">p95 (µs)</th>
                      <th className="py-2 px-3 text-right">p99 (µs)</th>
                      <th className="py-2 px-3 text-right">Max (µs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latest.hot_paths.map((hp) => (
                      <HotPathRow key={hp.hotpath_name} hp={hp} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2 text-amber-400">
              Histórico de snapshots ({history.length})
            </h2>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500">Sem histórico.</p>
            ) : (
              <div className="overflow-x-auto bg-[#161a1f] rounded border border-[#2a2f36]">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d0f12] text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="py-2 px-3 text-left">Captured at</th>
                      <th className="py-2 px-3 text-left">SDK version</th>
                      <th className="py-2 px-3 text-right">RSS (MB)</th>
                      <th className="py-2 px-3 text-right">Threads</th>
                      <th className="py-2 px-3 text-right">Uptime (s)</th>
                      <th className="py-2 px-3 text-right">Hot paths</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const hpCount = h.perf_snapshot?.samples
                        ? Object.keys(h.perf_snapshot.samples).length
                        : 0;
                      return (
                        <tr
                          key={`${h.captured_at}-${i}`}
                          className="border-b border-[#2a2f36] hover:bg-[#1a1f26]"
                        >
                          <td className="py-2 px-3 font-mono text-xs">{formatDateTime(h.captured_at)}</td>
                          <td className="py-2 px-3 font-mono text-xs">{h.sdk_version || '—'}</td>
                          <td className="py-2 px-3 text-right text-gray-300">
                            {formatNumber(h.process_metrics.rss_mb, 2)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-300">
                            {formatNumber(h.process_metrics.thread_count)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-300">
                            {formatNumber(h.process_metrics.uptime_s)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-300">{hpCount}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {!latest && !loading && !error && searchedUuid && (
        <p className="text-sm text-gray-500">Sem snapshot pra esta sessão.</p>
      )}
    </div>
  );
}
